import React, { useEffect, useState } from 'react';
import { format, startOfDay } from 'date-fns';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import ClearIcon from '@material-ui/icons/Clear';
import TuneIcon from '@material-ui/icons/Tune';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import Chart from './Chart';
import Container from '../../Elements/Container';
import ErrorAlert from '../../common/ErrorAlert';
import TopNav from '../../common/TopNav';
import EventsCard from './EventsCard';
import Filter from './Filter';
import { getEvents } from '../../../services/meters';

const PAGEsIZEbACK = 500;

const useStyles = makeStyles(theme => ({
  btn: {
    margin: theme.spacing(2, 1),
    '& .MuiButton-label': {
      textTransform: 'none',
      fontWeight: 700,
      color: theme.palette.primary.main,
    },
    '&.MuiButton-root': {
      backgroundColor: theme.palette.common.white,
    },
  },
  devicesText: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    '@media (max-width: 700px)': {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  chip: {
    margin: theme.spacing(0.5),
    '@media (max-width: 700px)': {
      width: '200px',
    },
  },
  tuneIcon: {
    '& .MuiSvgIcon-root': {
      fontSize: '1.5em',
      paddingTop: theme.spacing(0.5),
    },
  },
  circular: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '15px',
  },
  pagination: {
    margin: 'auto',
  },
}));
function Events() {
  const classes = useStyles();

  // Variables de estado
  const [filterPanel, setFilterPanel] = useState(true);
  const [filterOptions, setFilterOptions] = useState({
    dispositivos: [],
    showAlarms: true,
    showConnectivityOutages: true,
    quickDateOption: 'HOY',
    showElectricOutages: true,
    dateFrom: new Date(),
    dateTo: new Date(),
  });
  const [loading, setLoading] = useState(false);
  const [eventsData, setEventsData] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [severity, setSeverity] = useState('error');

  const pad2 = n => String(n).padStart(2, '0');
  const fmtDMY = d =>
    `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

  const fetchEventsSafe = async url => {
    try {
      const res = await getEvents(url);
      return res?.data ?? { page: [] };
    } catch (error) {
      if (error?.response?.status === 404) {
        // sin eventos = OK para esa fuente
        return { page: [] };
      }
      // re-lanzamos otros errores
      throw error;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const {
        dispositivos,
        dateFrom,
        dateTo,
        showAlarms,
        showConnectivityOutages,
        showElectricOutages,
        selectedAlerts,
      } = filterOptions;

      const hasAlerts = (selectedAlerts?.length ?? 0) > 0;
      const hasDevices = (dispositivos?.length ?? 0) > 0;

      // si no hay nada que consultar, limpiamos y salimos
      if (!hasAlerts && !hasDevices) {
        setEventsData({ page: [] });
        return;
      }

      setLoading(true);
      setErrorMessage('');
      try {
        // ---- URLs
        const fromStr = fmtDMY(dateFrom);
        const toStr = fmtDMY(dateTo);

        let alertsUrl = null;
        if (hasAlerts) {
          const qsAlerts =
            selectedAlerts
              .map(a => `alerts_id=${encodeURIComponent(a.id)}`)
              .join('&') +
            `&alertas=True&result_alert=True&from=${fromStr}&to=${toStr}&page=1&per_page=${PAGEsIZEbACK}`;
          alertsUrl = qsAlerts;
        }

        let devicesUrl = null;
        if (hasDevices) {
          const qsDevices =
            dispositivos
              .map(n => `filter=${encodeURIComponent(n.id)}`)
              .join('&') +
            `&alarmas=${!!showAlarms}&eventos=${!!showAlarms}&conectividad=${!!showConnectivityOutages}&cortes=${!!showElectricOutages}` +
            `&from=${fromStr}&to=${toStr}&page=1&per_page=${PAGEsIZEbACK}`;
          devicesUrl = qsDevices;
        }

        const [alertsRes, devicesRes] = await Promise.all([
          alertsUrl ? fetchEventsSafe(alertsUrl) : { page: [] },
          devicesUrl ? fetchEventsSafe(devicesUrl) : { page: [] },
        ]);

        const pageAlerts = Array.isArray(alertsRes?.page) ? alertsRes.page : [];
        const pageDevices = Array.isArray(devicesRes?.page)
          ? devicesRes.page
          : [];
        const merged = [...pageAlerts, ...pageDevices];

        setEventsData({ page: merged });
        setSeverity('success');
        setErrorMessage(
          merged.length ? '' : 'Sin resultados para los filtros seleccionados.'
        );
      } catch (error) {
        setSeverity('error');
        if (error?.response?.data?.msg) {
          setErrorMessage(`${error.response.data.msg}.`);
        } else {
          setErrorMessage('Ocurrió un error inesperado');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filterOptions]);

  const handleSubmit = (e, filter) => {
    e.preventDefault();
    setEventsData([]);
    if (filter !== filterOptions) {
      // Borro los datos solo en caso de que haya habido cambios, así muestro el <CircularProgress/>
      setEventsData([]);
      //Oculto el mensaje de error en caso de estar mostrando uno
      setErrorMessage('');
    }
    //Cierro el panel de Filtros
    setFilterPanel(false);
    setFilterOptions(filter);
  };

  // Funciones
  const togglePanel = () => {
    setFilterPanel(!filterPanel);
  };

  const handleClosePanel = () => {
    setFilterPanel(false);
  };

  return (
    <>
      <TopNav titulo="Análisis" subtitulo="| Eventos">
        <Grid container justifyContent="flex-end">
          <Button
            className={classes.btn}
            type="button"
            onClick={togglePanel}
            size="small"
            startIcon={filterPanel ? <ClearIcon /> : <TuneIcon />}
          >
            | Filtrar
          </Button>
        </Grid>
      </TopNav>

      <Dialog
        open={filterPanel}
        onClose={handleClosePanel}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
        fullWidth
        disableScrollLock
      >
        <DialogTitle id="form-dialog-title">Filtrar</DialogTitle>
        <DialogContent>
          <Filter
            onSubmit={handleSubmit}
            filterOptions={filterOptions}
            setFilterPanel={setFilterPanel}
          />
        </DialogContent>
      </Dialog>
      <Container>
        {(filterOptions?.dispositivos?.length === 0 && filterOptions?.selectedAlerts?.length === 0) && (
          <Alert className={classes.alert} severity="info">
            Bienvenido a Eventos. En esta sección podrá consultar la información
            de eventos de sus dispositivos. Para comenzar haga click en el botón
            <span className={classes.tuneIcon}>
              <TuneIcon />
            </span>
            , ingrese los parámetros y haga click en CONSULTAR.
          </Alert>
        )}

        {/* Si tengo un error lo muestro */}
        {errorMessage !== '' && (
          <Box className={classes.alert}>
            <ErrorAlert
              setErrorMessage={setErrorMessage}
              message={errorMessage}
              severity={severity}
            />
          </Box>
        )}

        {filterOptions.dispositivos.length > 0 && (
          <Box>
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              flexWrap={'wrap'}
            >
              <p className={classes.devicesText}>Dispositivos a graficar:</p>
              {filterOptions?.dispositivos?.map(node => (
                <Chip
                  className={classes.chip}
                  key={node.id}
                  icon={<LabelImportantIcon style={{ fontSize: '18px' }} />}
                  label={node.nombre}
                  clickable
                  color="primary"
                  onClick={() => setFilterPanel(true)}
                />
              ))}
              <p className={classes.devicesText}>Alertas a graficar:</p>
              {filterOptions?.selectedAlerts?.map(alert => (
                <Chip
                  className={classes.chip}
                  key={alert.id}
                  icon={<LabelImportantIcon style={{ fontSize: '18px' }} />}
                  label={alert.name}
                  clickable
                  color="primary"
                  onClick={() => setFilterPanel(true)}
                />
              ))}
            </Box>
            <p className={classes.devicesText}>
              Eventos del {format(filterOptions.dateFrom, 'dd/MM/yyyy')}{' '}
              {startOfDay(filterOptions.dateFrom).toISOString() !==
                startOfDay(filterOptions.dateTo).toISOString()
                ? `al ${format(filterOptions.dateTo, 'dd/MM/yyyy')}`
                : ''}
            </p>
          </Box>
        )}
        {!eventsData.page ? (
          <>
            {loading && (
              <Box
                height={'300px'}
                display={'flex'}
                alignItems={'center'}
                className={classes.circular}
              >
                <CircularProgress />
              </Box>
            )}
          </>
        ) : (
          <>
            <Chart eventsData={eventsData} filterOptions={filterOptions} />
            <Grid container>
              <EventsCard
                title={'Alarmas más recientes'}
                backPage={eventsData.page}
                pageSizeBack={PAGEsIZEbACK}
              />
            </Grid>
          </>
        )}
      </Container>
    </>
  );
}

export default Events;
